import React, { useEffect } from "react";
import ReviewsSection from "../Component/ReviewsSection";
import rahul from "../assets/images/rahul.webp";
import logo from "../assets/logo/logo.png";
import hq from "../assets/images/logos/img2.webp";
import eventixLogo from "../assets/images/logos/eventix_logo.webp";
import ProjectSection from "../Component/ProjectSection";
import GallerySection from "../Component/GallerySection";
import HobbiesSection from "../Component/HobbiesSection";
import SkillsSection from "../Component/SkillsSection";
import ArticlesSection from "../Component/ArticlesSection";
import ContactForm from "../Component/ContactForm";
import { Helmet } from 'react-helmet-async';

const Homepage = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    }, observerOptions);
    const revealElements = document.querySelectorAll(".reveal");
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <Helmet>
        <title>Rahul Biswas | Web Developer • UI/UX Designer • Founder</title>
        <meta name="title" content="Rahul Biswas | Web Developer, UI/UX Designer & Founder" />
        <meta name="description" content="Rahul Biswas is a Web Developer and UI/UX Designer, Founder of ContactHQ and Eventix. Enrolled at IIT Patna for AI & ML." />
      </Helmet>
      <div className="flex flex-col w-full overflow-x-hidden pt-16">
        {/* --- Hero Section --- */}
        <section className="relative w-[90%] mx-auto min-h-[90vh] flex items-center justify-center py-20 lg:py-0 overflow-visible">
          <div
            className="absolute top-20 right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -z-10"
            style={{ animation: "blobPulse 8s infinite" }}
          ></div>
          <div className="absolute bottom-0 left-[-5%] w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl -z-10"></div>

          <div className="w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="flex flex-col gap-8 order-2 lg:order-1">
              <div className="space-y-4">
                <div className="reveal inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider w-fit">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  OPEN TO NEW OPPORTUNITIES
                </div>

                <h1 className="reveal reveal-delay-1 text-4xl md:text-5xl xl:text-7xl font-black font-display text-gray-900 dark:text-white leading-[1.1]">
                  Exploring ideas, learning fast, {" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-600 to-indigo-600">
                    building digital products
                  </span>
                </h1>

                <p className="reveal reveal-delay-2 text-lg text-gray-600 dark:text-gray-400 max-w-5xl leading-relaxed">
                  Currently focused on learning and building, I create intuitive user experiences and scalable digital products through modern frontend technologies and user-centered design.
                </p>
              </div>

              <div className="reveal reveal-delay-3 space-y-3">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  Founder of
                </p>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 flex items-start gap-4 p-4 bg-white dark:bg-surface-dark rounded-xl border border-gray-100 dark:border-gray-800 shadow-soft hover:shadow-hover transition-all hover:border-primary/20 group">
                    <div className="size-12 rounded-[30px] bg-primary/10 flex items-center justify-center text-primary shrink-0 group-hover:scale-110 transition-transform">
                      <img src={hq} alt="ContactHQ logo" width="48" height="48" loading="lazy" className="" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-base font-bold text-gray-900 dark:text-white font-display">
                        ContactHQ
                      </span>
                      <span className="text-xs text-gray-500 mt-1 leading-snug">
                        AI-powered form builder & communication platform.
                        Learning, experimenting, and shipping real-world SaaS features.
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 flex items-start gap-4 p-4 bg-white dark:bg-surface-dark rounded-xl border border-gray-100 dark:border-gray-800 shadow-soft hover:shadow-hover transition-all hover:border-primary/20 group">
                    <div className="size-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 shrink-0 group-hover:scale-110 transition-transform">
                      <img src={eventixLogo} alt="Eventix logo" width="48" height="48" loading="lazy" className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-base font-bold text-gray-900 dark:text-white font-display">
                        Eventix
                      </span>
                      <span className="text-xs text-gray-500 mt-1 leading-snug">
                        Event management marketplace and SaaS platform scaling event-tech across India and Nepal.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="reveal reveal-delay-3 flex items-center gap-4 pt-4">
                <a
                  className="flex items-center justify-center h-14 px-5 sm:px-10 bg-primary hover:bg-primary-dark text-white text-base font-bold rounded-xl transition-all transform hover:-translate-y-1 shadow-lg shadow-primary/25 font-display"
                  href="#projects"
                >
                  View My Work
                </a>
                <a
                  className="flex items-center justify-center h-14 px-4 sm:px-8 bg-transparent border-2 border-gray-200 dark:border-gray-700 hover:border-primary text-gray-900 dark:text-white text-base font-bold rounded-xl transition-all font-display hover:text-primary"
                  href="#contact"
                >
                  Get in Touch
                </a>
              </div>
            </div>

            <div className="relative order-1 lg:order-2 flex justify-center lg:justify-end reveal">
              <div className="floating-card relative w-full max-w-[480px] aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 ease-out">
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent z-10"></div>
                <img
                  alt="Rahul Biswas - Software Engineer"
                  className="w-full h-full object-cover"
                  src={rahul}
                  fetchpriority="high"
                  decoding="async"
                  width="480"
                  height="600"
                />

                <div className="absolute bottom-8 left-8 z-20">
                  <p className="text-white/70 text-sm font-medium mb-1 font-display tracking-wide uppercase">
                    Software Engineer
                  </p>
                  <h3 className="text-white text-3xl font-bold font-display tracking-tight">
                    Rahul Biswas
                  </h3>
                </div>
              </div>

              <div
                className="absolute -bottom-6 -right-2 sm:-right-6 w-20 h-20 sm:w-24 md:h-24 bg-primary rounded-[50%] flex items-center justify-center shadow-glow z-30 animate-bounce"
                style={{ animationDuration: "4s" }}
              >
                <img src={logo} alt="logo" />
              </div>
            </div>
          </div>
        </section>

        {/* --- About Section --- */}
        <section
          id="about"
          className="relative py-32 bg-white dark:bg-surface-dark border-y border-gray-100 dark:border-gray-800 scroll-mt-20 overflow-hidden"
        >
          <div className="absolute top-1/4 -left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10"></div>

          <div className="w-[90%] mx-auto">
            <div className="flex flex-col lg:flex-row gap-16 items-start">
              <div className="lg:w-3/5 space-y-4">
                <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  The Persona
                </div>

                <h2 className="reveal reveal-delay-1 text-4xl md:text-6xl font-bold text-gray-900 dark:text-white leading-tight font-display">
                  Bridging {" "}
                  <span className="text-primary italic">logical engineering and</span>{" "}
                  creative aesthetics.
                </h2>

                <div className="reveal reveal-delay-2 grid md:grid-cols-2 gap-8 pt-4">
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                    I'm a web developer and UI/UX designer with a {" "}
                    <span className="text-gray-900 dark:text-white font-semibold underline decoration-primary/30 decoration-2">
                      product-first mindset, driven by curiosity and clarity.
                    </span>{" "}
                    I enjoy working at the intersection of <span className="text-gray-900 dark:text-white font-semibold underline decoration-primary/30 decoration-2">problem-solving, visual design, and meaningful user experiences. </span> where thoughtful design meets <span className="text-gray-900 dark:text-white font-semibold underline decoration-primary/30 decoration-2">clean, scalable code.</span>
                  </p>
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed border-l-2 border-primary/20 pl-6">
                    Based in India, I build SaaS products like{" "}
                    <span className="text-primary font-medium">ContactHQ</span>{" "}
                    and build event-tech platforms such as {" "}
                    <span className="text-primary font-medium">Eventix</span>.
                    My focus is on creating digital products where technical precision meets human emotion-simple to use, purposeful by design, and built to scale.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Critical Thinking
                  </span>

                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Lifelong Learning
                  </span>

                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Mental Clarity
                  </span>

                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Creative Exploration
                  </span>

                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Emotional Awareness
                  </span>

                  <span className="px-4 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm font-medium text-gray-500 border border-gray-100 dark:border-gray-800 hover:border-primary/30 hover:text-primary transition-colors cursor-default">
                    Curiosity Driven
                  </span>
                </div>

              </div>

              <div className="lg:w-2/5 w-full grid grid-cols-1 sm:grid-cols-2 gap-4 reveal reveal-delay-2">
                <div className="group p-8 rounded-3xl bg-gray-50/50 dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary/30 transition-all duration-500 hover:shadow-hover">
                  <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <span className="material-symbols-outlined">terminal</span>
                  </div>
                  <span className="block text-4xl font-black font-display text-gray-900 dark:text-white mb-1">
                    3+
                  </span>
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-tighter">
                    YEARS OF CODING
                  </span>
                </div>

                <div className="group p-8 rounded-3xl bg-gray-50/50 dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary/30 transition-all duration-500 hover:shadow-hover mt-4 sm:mt-12">
                  <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <span className="material-symbols-outlined">
                      deployed_code
                    </span>
                  </div>
                  <span className="block text-4xl font-black font-display text-gray-900 dark:text-white mb-1">
                    100+
                  </span>
                  <span className="text-xs text-gray-400 font-bold uppercase tracking-tighter">
                    ACTIVE PROJECTS
                  </span>
                </div>

                <div className="group p-8 rounded-3xl bg-primary text-white border border-transparent transition-all duration-500 shadow-glow hover:-translate-y-2 sm:col-span-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="block text-4xl font-black font-display mb-1">
                        100%
                      </span>
                      <span className="text-xs text-white/70 font-bold uppercase tracking-tighter">
                        SELF-MOTIVATED
                      </span>
                    </div>
                    <span className="material-symbols-outlined text-4xl opacity-50">
                      bolt
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-white/80 leading-snug">
                    Driven by curiosity, continuous learning, and a strong desire
                    to build thoughtful digital products that make a real impact.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- Skills Section --- */}
        <section
          id="skills"
          className="py-24 bg-background-light dark:bg-background-dark border-b border-gray-100 dark:border-gray-800 scroll-mt-20"
        >
          <div className="w-[90%] mx-auto">
            {/* --- Header: Responsive alignment --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
              <div>
                <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  My Arsenal
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white font-display tracking-tight">
                  Skills & Expertise
                </h2>
              </div>

              <p className="reveal reveal-delay-1 text-gray-600 dark:text-gray-400 max-w-md text-left md:text-right text-lg">
                A focused set of skills I use to learn fast,{" "}
                <span className="text-primary font-medium">ship thoughtfully</span> and{" "}
                <span className="text-primary font-medium">grow real products</span>.
              </p>
            </div>

            {/* --- Responsive Grid: 1 col mobile, 2 col tablet, 4 col desktop --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:auto-rows-[190px] mb-16">

              {/* 1. Product Design (Spans 2 cols on MD+ and 2 rows on LG+) */}
              <div className="reveal reveal-delay-1 md:col-span-2 md:row-span-2 rounded-3xl bg-white dark:bg-surface-dark p-6 md:p-8 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group relative overflow-hidden flex flex-col justify-between h-full">
                <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-150 duration-700"></div>
                <div className="relative z-10">
                  <div className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 transform group-hover:rotate-6 transition-transform">
                    <span className="material-symbols-outlined text-3xl">brush</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 dark:text-white font-display">
                    Product Design
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm md:text-base">
                    I work at the intersection of usability, clarity, and scalability. With a strong foundation in UI/UX principles and design systems, I translate complex ideas into intuitive interfaces that feel simple, purposeful, and human.
                  </p>
                </div>
                <div className="flex gap-2 mt-8 flex-wrap relative z-10">
                  {["UI, Prototyping, Systems Design", "Flows, Brainstorming, Wireframing", "Research & Mapping", "Product & Planning"].map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-gray-50 dark:bg-gray-800 rounded-lg text-[10px] md:text-xs font-bold dark:text-gray-300 border border-gray-100 dark:border-gray-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* 2. UI/UX Design */}
              <div className="reveal reveal-delay-3 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined">animation</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">UI/UX Design</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Designing intuitive, scalable interfaces focused on clarity, usability, and real user needs.
                </p>
              </div>

              {/* 3. Frontend Dev */}
              <div className="reveal reveal-delay-2 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300 shadow-glow">
                  <span className="material-symbols-outlined">code</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">Frontend Dev</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Building fast, accessible React & Next.js interfaces.
                </p>
              </div>

              {/* 4. Backend */}
              <div className="reveal reveal-delay-2 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined">database</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">Backend</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Designing scalable APIs and reliable backend systems.
                </p>
              </div>

              {/* 5. Motion & Interaction */}
              <div className="reveal reveal-delay-3 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined">animation</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">Motion & Interaction</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Purposeful motion that guides, not distracts.
                </p>
              </div>

              {/* 6. Product Strategy (Spans 2 cols on MD+) */}
              <div className="reveal reveal-delay-3 md:col-span-2 rounded-3xl bg-primary dark:bg-primary p-6 md:p-8 shadow-glow hover:shadow-hover transition-all duration-500 group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div className="flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-white mb-2 font-display">
                    Product Strategy
                  </h3>
                  <p className="text-white/70 text-sm md:text-base max-w-lg leading-snug">
                    I approach products holistically-balancing user needs, business goals, and technical feasibility. Turning ideas into systems that scale.
                  </p>
                </div>
                <div className="size-16 md:size-20 shrink-0 rounded-2xl bg-white/10 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all self-end sm:self-center">
                  <span className="material-symbols-outlined text-white text-4xl md:text-5xl">lightbulb</span>
                </div>
              </div>

              {/* 7. Content & Communication */}
              <div className="reveal reveal-delay-3 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined">edit_note</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">Content</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Clear UX copy and structured technical writing.
                </p>
              </div>

              {/* 8. Testing */}
              <div className="reveal reveal-delay-3 rounded-3xl bg-white dark:bg-surface-dark p-6 shadow-soft hover:shadow-hover transition-all duration-500 border border-gray-100 dark:border-gray-800 group hover:-translate-y-2">
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined">edit_note</span>
                </div>
                <h3 className="text-xl font-bold mb-2 dark:text-white font-display">Testing</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Validating ideas through usability testing and feedback loops.
                </p>
              </div>

            </div>

            {/* Full Width Tech Stack Card */}
            <SkillsSection />
          </div>
        </section>

        {/* --- Education Section --- */}
        <section
          id="education"
          className="relative py-32 bg-white dark:bg-surface-dark border-t border-gray-100 dark:border-gray-800 scroll-mt-20 overflow-hidden"
        >
          <div className="absolute top-1/2 -left-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl -z-10"></div>

          <div className="w-[90%] mx-auto">
            <div className="flex flex-col items-center text-center mb-20">
              <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Academic Background
              </div>
              <h2 className="reveal reveal-delay-1 text-4xl md:text-5xl font-bold text-gray-900 dark:text-white font-display tracking-tight">
                Education & Achievements
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

              {/* AI / ML - ACTIVE CARD */}
              <div className="reveal reveal-delay-1 group p-8 rounded-3xl bg-primary text-white border border-transparent shadow-glow transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div className="size-12 rounded-xl bg-white/20 flex items-center justify-center transform group-hover:rotate-6 transition-transform">
                      <span className="material-symbols-outlined text-2xl text-white">
                        psychology
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-white/60 uppercase tracking-widest">
                      2026 – 2027
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 font-display leading-tight">
                    Vishlesan i-Hub, IIT Patna
                  </h3>
                  <p className="text-white/80 font-bold text-xs mb-4 uppercase">
                    AI & Machine Learning Program
                  </p>
                </div>
                <p className="text-sm text-white/70 leading-relaxed italic">
                  Applied AI, machine learning fundamentals, data analysis, and real-world problem solving through research-driven learning.
                </p>
              </div>

              {/* Full Stack */}
              <div className="reveal reveal-delay-2 group p-8 rounded-3xl bg-background-light dark:bg-background-dark border border-gray-100 dark:border-gray-800 hover:border-primary/30 transition-all duration-500 hover:shadow-hover flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center transform group-hover:-rotate-6 transition-transform">
                      <span className="material-symbols-outlined text-2xl">
                        code
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      2022 – 2023
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 font-display leading-tight">
                    Newton School
                  </h3>
                  <p className="text-primary font-bold text-xs mb-4 uppercase">
                    Full-Stack Development
                  </p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic">
                  Hands-on training in React, JavaScript, backend APIs, system thinking, and production-ready project development.
                </p>
              </div>

              {/* Diploma */}
              <div className="reveal reveal-delay-3 group p-8 rounded-3xl bg-background-light dark:bg-background-dark border border-gray-100 dark:border-gray-800 hover:border-primary/30 transition-all duration-500 hover:shadow-hover flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <span className="material-symbols-outlined text-2xl">
                        engineering
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      2017 – 2019
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 font-display leading-tight">
                    Dumkal IT
                  </h3>
                  <p className="text-primary font-bold text-xs mb-4 uppercase">
                    Diploma in Civil Engineering
                  </p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic">
                  Built analytical thinking, structural logic, and engineering problem-solving foundations.
                </p>
              </div>

              {/* Higher Secondary - NORMAL CARD NOW */}
              <div className="reveal reveal-delay-4 group p-8 rounded-3xl bg-background-light dark:bg-background-dark border border-gray-100 dark:border-gray-800 hover:border-primary/30 transition-all duration-500 hover:shadow-hover flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center transform group-hover:rotate-12 transition-transform">
                      <span className="material-symbols-outlined text-2xl">
                        school
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      2015 – 2016
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 font-display leading-tight">
                    B N High School, Jamshedpur
                  </h3>
                  <p className="text-primary font-bold text-xs mb-4 uppercase">
                    Higher Secondary Education
                  </p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic">
                  Academic foundation with early exposure to logic, mathematics, and structured learning.
                </p>
              </div>

            </div>



            <div className="reveal reveal-delay-4 mt-16 pt-12 border-t border-gray-100 dark:border-gray-800 flex flex-wrap justify-center items-center gap-10 opacity-60 hover:opacity-100 transition-opacity">
              <div className="flex items-center gap-2 group cursor-default">
                <span className="material-symbols-outlined text-primary group-hover:scale-125 transition-transform">
                  school
                </span>
                <span className="text-xs font-bold uppercase tracking-widest dark:text-white">
                  FULL-STACK CERTIFICATION
                </span>
              </div>
              <div className="flex items-center gap-2 group cursor-default">
                <span className="material-symbols-outlined text-primary group-hover:scale-125 transition-transform">
                  design_services
                </span>
                <span className="text-xs font-bold uppercase tracking-widest dark:text-white">
                  PRODUCT DESIGN CERTIFIED
                </span>
              </div>
              <div className="flex items-center gap-2 group cursor-default">
                <span className="material-symbols-outlined text-primary group-hover:scale-125 transition-transform">
                  emoji_events
                </span>
                <span className="text-xs font-bold uppercase tracking-widest dark:text-white">
                  EMPLOYEE OF THE QUARTER
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* --- Experience Section --- */}
        <section
          id="experience"
          className="py-24 bg-background-light dark:bg-background-dark border-y border-[#e7ebf3] dark:border-gray-800 scroll-mt-20 overflow-hidden"
        >
          <div className="w-[90%] mx-auto">
            <div className="flex flex-col items-center text-center mb-20">
              <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                My Path
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl md:text-5xl font-bold text-[#0d121b] dark:text-white mt-4 font-display">
                Experience & Journey
              </h2>
            </div>

            <div className="relative max-w-[1100px] mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 -translate-x-1/2"></div>

              {/* Eventix */}
              <div className="reveal reveal-delay-1 relative z-10 flex flex-col md:flex-row items-center justify-between mb-16 group">
                <div className="order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Founder & Startup Strategist
                  </h3>
                  <p className="text-primary font-bold">Eventix</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    An event management marketplace and SaaS platform designed to scale event-tech solutions across India and Nepal. Focused on local SEO optimization and business evolution for organizers in regions like Siliguri, Darjeeling, and Kathmandu.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-primary rounded-full shadow-glow group-hover:scale-125 transition-all duration-500">
                  <span className="size-2 bg-primary rounded-full animate-pulse"></span>
                </div>
                <div className="order-1 md:w-[45%] pl-12 md:pl-10 text-primary font-black text-xs uppercase tracking-widest">
                  Jan 2026 - Present
                </div>
              </div>

              {/* ContactHQ */}
              <div className="reveal reveal-delay-2 relative z-10 flex flex-col md:flex-row items-center justify-between mb-16 group">
                <div className="order-1 md:order-2 md:w-[45%] text-left pl-12 md:pl-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Product Builder
                  </h3>
                  <p className="text-primary font-bold">ContactHQ</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    Building an AI-powered form builder and unified inbox. Handling product
                    design, frontend architecture, and early SaaS strategy.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-gray-300 dark:border-gray-700 rounded-full shadow-lg group-hover:border-primary transition-colors duration-500"></div>
                <div className="order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10 text-gray-400 dark:text-gray-500 font-bold text-xs uppercase tracking-widest">
                  Sep 2025 - Present
                </div>
              </div>

              {/* SRV Media */}
              <div className="reveal reveal-delay-3 relative z-10 flex flex-col md:flex-row items-center justify-between mb-16 group">
                <div className="order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Frontend Developer
                  </h3>
                  <p className="text-primary font-bold">SRV Media</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    Building production-ready user interfaces with React and modern frontend
                    tools, focusing on performance and usability.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-gray-300 dark:border-gray-700 rounded-full shadow-lg group-hover:border-primary transition-colors duration-500"></div>
                <div className="order-1 md:w-[45%] pl-12 md:pl-10 text-gray-400 dark:text-gray-500 font-bold text-xs uppercase tracking-widest">
                  Jan 2024 - Present
                </div>
              </div>

              {/* TechPax */}
              <div className="reveal reveal-delay-4 relative z-10 flex flex-col md:flex-row items-center justify-between mb-16 group">
                <div className="order-1 md:order-2 md:w-[45%] text-left pl-12 md:pl-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Web Developer
                  </h3>
                  <p className="text-primary font-bold">TechPax</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    Developed responsive websites and UI components, translating business
                    needs into clean, maintainable frontend code.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-gray-300 dark:border-gray-700 rounded-full shadow-lg group-hover:border-primary transition-colors duration-500"></div>
                <div className="order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10 text-gray-400 dark:text-gray-500 font-bold text-xs uppercase tracking-widest">
                  Mar 2023 - Nov 2023
                </div>
              </div>

              {/* Newton School */}
              <div className="reveal reveal-delay-5 relative z-10 flex flex-col md:flex-row items-center justify-between mb-16 group">
                <div className="order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Full Stack Developer Intern
                  </h3>
                  <p className="text-primary font-bold">Newton School</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    Hands-on experience building full-stack applications using JavaScript,
                    React, APIs, and backend fundamentals.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-gray-300 dark:border-gray-700 rounded-full shadow-lg group-hover:border-primary transition-colors duration-500"></div>
                <div className="order-1 md:w-[45%] pl-12 md:pl-10 text-gray-400 dark:text-gray-500 font-bold text-xs uppercase tracking-widest">
                  Mar 2022 - Nov 2022
                </div>
              </div>

              {/* Pegasuswave */}
              <div className="reveal reveal-delay-6 relative z-10 flex flex-col md:flex-row items-center justify-between group">
                <div className="order-1 md:order-2 md:w-[45%] text-left pl-12 md:pl-10">
                  <h3 className="text-xl font-bold text-[#0d121b] dark:text-white font-display uppercase tracking-tight">
                    Customer Executive
                  </h3>
                  <p className="text-primary font-bold">Pegasuswave</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                    Worked directly with customers, resolving issues and improving service
                    experience - building a strong user-first mindset.
                  </p>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center size-8 bg-white dark:bg-surface-dark border-4 border-gray-300 dark:border-gray-700 rounded-full shadow-lg group-hover:border-primary transition-colors duration-500"></div>
                <div className="order-1 md:order-1 md:w-[45%] text-left md:text-right pl-12 md:pl-0 md:pr-10 text-gray-400 dark:text-gray-500 font-bold text-xs uppercase tracking-widest">
                  May 2021 - Dec 2021
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* --- Projects Section --- */}
        <ProjectSection />

        {/* --- Hobbies Section --- */}
        <HobbiesSection />

        {/* --- Gallery Section --- */}
        <GallerySection />

        {/* --- Articles Section --- */}
        <ArticlesSection />

        {/* --- Reviews Section --- */}
        <ReviewsSection />

        {/* --- Contact Section --- */}
        <section
          id="contact"
          className="relative py-32 bg-[#0d121b] dark:bg-black text-white overflow-hidden scroll-mt-20"
        >
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4 -z-0"></div>

          <div className="w-[90%] mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div className="space-y-8">
                <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest font-display">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  Let's Connect
                </div>

                <h2 className="reveal reveal-delay-1 text-5xl md:text-7xl font-bold font-display leading-[1.1] tracking-tight">
                  Let's build something <span className="text-primary">legendary.</span>
                </h2>

                <div className="reveal reveal-delay-3 space-y-6 pt-4">
                  <a
                    className="flex items-center gap-5 text-xl font-medium hover:text-primary transition-all group font-display"
                    href="mailto:rahul@contacthq.in"
                  >
                    <span className="size-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:border-primary/50 transition-all">
                      <span className="material-symbols-outlined text-primary text-3xl">
                        mail
                      </span>
                    </span>
                    rahul@contacthq.in
                  </a>

                  <div className="flex flex-wrap gap-4 pt-4">
                    {/* LinkedIn */}
                    <a
                      href="https://www.linkedin.com/in/rahulbiswas0675/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#0077b5] hover:border-[#0077b5] flex items-center justify-center transition-all group"
                      title="LinkedIn"
                    >
                      <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>

                    {/* Instagram */}
                    <a
                      href="https://www.instagram.com/rahul.lens.ig/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#e1306c] hover:border-[#e1306c] flex items-center justify-center transition-all group"
                      title="Instagram"
                    >
                      <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>

                    {/* X (Twitter) */}
                    <a
                      href="https://x.com/RahulBiswas0675"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-white hover:border-white flex items-center justify-center transition-all group"
                      title="X (Twitter)"
                    >
                      <svg className="w-5 h-5 fill-white group-hover:fill-black group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298l13.309 17.41z" />
                      </svg>
                    </a>

                    {/* YouTube */}
                    <a
                      href="https://www.youtube.com/@B4_I_Sleep"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#ff0000] hover:border-[#ff0000] flex items-center justify-center transition-all group"
                      title="YouTube"
                    >
                      <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </a>

                    {/* Facebook */}
                    <a
                      href="https://www.facebook.com/rahulbiswas0675"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-14 rounded-2xl bg-white/5 border border-white/10 hover:bg-[#1877f2] hover:border-[#1877f2] flex items-center justify-center transition-all group"
                      title="Facebook"
                    >
                      <svg className="w-6 h-6 fill-white group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              <div className="reveal reveal-delay-2 relative">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Homepage;